/** tm/pages/GovPage.tsx — 정부지원사업 (3컬럼, 지역+분류 매칭) */
import { canonCat, catOrder } from '../components/GovTaxonomy';
import { useState, useMemo, useEffect, useCallback, useRef, memo } from 'react';
import type { TMStore } from '../hooks';
import { fmtDate } from '../hooks';
import { govPeriodSms } from '../components/govPeriod';
import { C, T, R, Badge, DDayBadge, Btn, SearchInput, Empty } from '../components/ui';
import GovProgramForm, {
  GOV_CATEGORIES, GOV_ARTS, emptyGovProgramForm, govProgramFormFromRow, govProgramFormToPayload, canSaveGovForm, conditionsFromRow,
  type GovProgramFormState,
} from '../components/GovProgramForm';
import { tracksSummary } from '../components/govRuleText';
import * as api from '../api';
import type { GovProgram, FP } from '../types';
import { ceoContact } from '../types';
import GovFeedInbox from './GovFeedInbox';
import GovFeedDocViewer from '../components/GovFeedDocViewer';
import { GovViewSwitch, GovTopBar, splitRegions, regionRank, REGION_ORDER, NATIONWIDE, type GovView } from '../components/GovFeedParts';
import GovSharePage from './GovSharePage';
import GovTargetReview from './GovTargetReview';
import GovTargetCheckPage from './GovTargetCheckPage';
import { TargetChip, targetText } from '../components/GovTargetChips';
import GovProgGroupBox from '../components/GovProgGroupBox';

interface Props { store: TMStore; senderName?: string }

const secTitle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: C.sub, marginBottom: 8, paddingBottom: 6, borderBottom: `1px solid ${C.border}` };
const filterItem = (active: boolean): React.CSSProperties => ({
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '6px 10px', borderRadius: R.sm, cursor: 'pointer', fontSize: 12,
  fontWeight: active ? 600 : 400, color: active ? C.accent : C.text,
  background: active ? C.accentLt : 'transparent', transition: 'all 0.12s',
});

/* ── 같은 공고 묶음 · 상품 아닌 공고 (2026-10-05 · 148 · backend services/govDup.js) · 같은 사업 묶음 (2026-10-07 · 151 prog_group) ── */
type DupFilter = 'all' | 'review' | 'grouped' | 'proggrp' | 'hidden';
/** 같은 사업 묶음(prog_group) 식구 수 — 목록 카드의 「같은 사업 N건」 · 「상태 › 같은 사업 묶음」 칸 (혼자는 묶음이 아니다) */
function progGroupCounts(list: GovProgram[]): Map<string, number> {
  const m = new Map<string, number>();
  list.forEach(g => { if (g.prog_group) m.set(g.prog_group, (m.get(g.prog_group) || 0) + 1); });
  return m;
}
const HIDDEN_KIND_LABEL: Record<string, { label: string; title: string }> = {
  agency_recruit: { label: '기관 모집', title: '기업이 받는 지원이 아니라 운영·수행기관 모집 — 매칭 · 리포트 · 단톡방에서 뺍니다' },
  umbrella: { label: '통합 공고', title: '세부 상품을 묶기만 한 통합 공고 — 세부 상품이 따로 등록돼 있어 매칭 · 리포트에서 뺍니다' },
  umbrella_unsplit: { label: '통합 공고 · 나눌 것', title: '통합 공고인데 세부 상품이 아직 따로 없어 그대로 보입니다 — 세부 상품으로 나눠 등록하면 자동으로 가려집니다' },
};
/** 매칭 · 리포트에서 빠지는 종류 (backend govDup.HIDDEN_KINDS 와 같다 — umbrella_unsplit 은 보인다) */
const isHiddenKind = (g: GovProgram) => !g.hide_off && (g.hidden_kind === 'agency_recruit' || g.hidden_kind === 'umbrella');
/** 목록 한 줄 = 묶음 하나 — 대표만 남기고 식구는 접는다 (대표가 목록에 없으면 마감이 가장 늦은 식구가 대신 선다) */
function foldDupGroups(list: GovProgram[]): { visible: GovProgram[]; kidsOf: Map<string, GovProgram[]>; repOf: Map<string, string> } {
  const groups = new Map<string, GovProgram[]>();
  list.forEach(g => { if (g.dup_group && !g.dup_off) { const k = String(g.dup_group); const a = groups.get(k); if (a) a.push(g); else groups.set(k, [g]); } });
  const kidsOf = new Map<string, GovProgram[]>();
  const repOf = new Map<string, string>();
  const folded = new Set<string>();
  groups.forEach((rows, k) => {
    if (rows.length < 2) return;
    const rep = rows.find(r => r.dup_primary !== false && r.id === k) || rows.find(r => r.dup_primary !== false)
      || rows.slice().sort((a, b) => (b.deadline || '9999').localeCompare(a.deadline || '9999'))[0];
    const kids = rows.filter(r => r !== rep);
    kidsOf.set(rep.id, kids);
    kids.forEach(r => { folded.add(r.id); repOf.set(r.id, rep.id); });
  });
  return { visible: list.filter(g => !folded.has(g.id)), kidsOf, repOf };
}
const dupBtn = (color: string, bg: string, border: string): React.CSSProperties => ({ height: 24, padding: '0 9px', borderRadius: R.sm, border: `1px solid ${border}`, background: bg, color, fontSize: T.xs, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' });

/** 통신사 SMS 90바이트(EUC-KR) 한도 — backend/services/smsText.js 와 같은 코드포인트 기준 계산 */
function smsByteLen(text: string): number {
  let n = 0;
  for (const ch of text) n += (ch.codePointAt(0) || 0) > 0x7f ? 2 : 1;
  return n;
}
function smsMsgType(text: string): 'SMS' | 'LMS' { return smsByteLen(text) <= 90 ? 'SMS' : 'LMS'; }

interface SendItem { key: string; label: string; phone: string; message: string; count?: number }

/* ── 등록된 공고 정렬 (2026-10-03) — 목록이 카드형이라 헤더 대신 툴바의 「정렬」 칩으로.
 *    한 번 → 오름차순, 두 번 → 내림차순, 세 번 → 기본(마감 임박순). 빈 값은 방향과 무관하게 항상 맨 뒤. */
type ProgSortKey = 'name' | 'region' | 'category' | 'amount' | 'deadline';
type ProgSort = { key: ProgSortKey; dir: 'asc' | 'desc' } | null;
const PROG_SORT_LS = 'gov.programs.sort.v1';
const PROG_SORT_COLS: { key: ProgSortKey; label: string; title: string }[] = [
  { key: 'name', label: '사업명', title: '사업명 가나다순' },
  { key: 'region', label: '지역', title: '전국 → 서울·경기·인천 … 제주 → 시군구' },
  { key: 'category', label: '분류', title: '분류 체계 순서' },
  { key: 'amount', label: '금액', title: '지원금액(만원 숫자, 없으면 금액 문구에서 추정)' },
  { key: 'deadline', label: '마감', title: '마감일 — 선착순·미정은 맨 뒤' },
];

function loadProgSort(): ProgSort {
  try {
    const raw = window.localStorage.getItem(PROG_SORT_LS);
    if (!raw) return null;
    const v: unknown = JSON.parse(raw);
    if (v && typeof v === 'object') {
      const o = v as { key?: unknown; dir?: unknown };
      if (PROG_SORT_COLS.some(c => c.key === o.key) && (o.dir === 'asc' || o.dir === 'desc')) return { key: o.key as ProgSortKey, dir: o.dir };
    }
  } catch { /* 저장소 차단·손상 — 기본 정렬 */ }
  return null;
}
function saveProgSort(s: ProgSort) {
  try {
    if (s) window.localStorage.setItem(PROG_SORT_LS, JSON.stringify(s));
    else window.localStorage.removeItem(PROG_SORT_LS);
  } catch { /* 무시 */ }
}

/** 금액 문구 → 만원. 「최대 1억원」「5,000만원」「1억 5천만원」「3천만원」 — 여러 금액이면 가장 큰 값. 못 읽으면 null */
function parseAmountManwon(text: string | null | undefined): number | null {
  if (!text) return null;
  const s = text.replace(/,/g, '');
  const re = /(\d+(?:\.\d+)?)\s*(억|천만|백만|만|천)?\s*(원)?/g;
  const groups: number[] = [];
  let prevUnit = ''; let prevEnd = -10;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) {
    if (m[0] === '') { re.lastIndex++; continue; }
    const n = Number(m[1]); const unit = m[2] || ''; const won = !!m[3];
    let v: number | null = null;
    if (unit === '억') v = n * 10000;
    else if (unit === '천만') v = n * 1000;
    else if (unit === '백만') v = n * 100;
    else if (unit === '만') v = n;
    else if (unit === '천') v = prevUnit === '억' && m.index - prevEnd <= 2 ? n * 1000 : (won ? n / 10 : null); // 「1억5천」= 1억 5천만
    else if (won) v = n / 10000;
    if (v === null) { prevUnit = ''; continue; }
    // 「1억 5천만원」처럼 억 바로 뒤의 작은 단위는 앞 금액에 더한다
    if (prevUnit === '억' && unit !== '억' && m.index - prevEnd <= 2 && groups.length) groups[groups.length - 1] += v;
    else groups.push(v);
    prevUnit = unit; prevEnd = m.index + m[0].length;
  }
  return groups.length ? Math.max(...groups) : null;
}

/** 정렬 키 — [단계, 값]. 단계가 클수록 항상 뒤(빈 값), 같은 단계 안에서만 방향을 따른다 */
function progSortValue(g: GovProgram, key: ProgSortKey): { tier: number; v: number | string } {
  switch (key) {
    case 'name': {
      const n = (g.name || g.report_name || '').trim();
      return n ? { tier: 0, v: n } : { tier: 2, v: '' };
    }
    case 'region': {
      const rs = splitRegions(g.region); // 지역 칸 분리 규칙은 왼쪽 「지역」 칸과 같다 (GovFeedParts.splitRegions)
      if (rs.length === 0) return { tier: 0, v: '000' }; // 전국 = 맨 앞
      const rank = Math.min(...rs.map(regionRank));
      return { tier: 0, v: `${String(rank + 1).padStart(3, '0')}${(g.region_detail || '').trim()}` };
    }
    case 'category': {
      const cs = (g.category || '').split(',').map(s => s.trim()).filter(Boolean);
      return cs.length ? { tier: 0, v: Math.min(...cs.map(c => catOrder(c))) } : { tier: 2, v: 0 };
    }
    case 'amount': {
      const n = g.amount_max_manwon != null ? Number(g.amount_max_manwon) : parseAmountManwon(g.amount);
      return n != null && Number.isFinite(n) ? { tier: 0, v: n } : { tier: 2, v: 0 };
    }
    case 'deadline': {
      const d = (g.deadline || '').slice(0, 10);
      if (d) return { tier: 0, v: d };
      return g.first_come ? { tier: 1, v: '' } : { tier: 2, v: '' }; // 선착순 → 미정 순으로 맨 뒤
    }
  }
}

function sortPrograms(list: GovProgram[], sort: ProgSort): GovProgram[] {
  if (!sort) return list;
  const sign = sort.dir === 'asc' ? 1 : -1;
  return list
    .map((g, i) => ({ g, i, k: progSortValue(g, sort.key) }))
    .sort((a, b) => {
      if (a.k.tier !== b.k.tier) return a.k.tier - b.k.tier;
      const c = typeof a.k.v === 'number' && typeof b.k.v === 'number'
        ? a.k.v - b.k.v
        : String(a.k.v).localeCompare(String(b.k.v), 'ko');
      return c !== 0 ? c * sign : a.i - b.i; // 동률은 기본 순서 유지
    })
    .map(x => x.g);
}

/* 공고 카드 (2026-10-05s 지도사 「상세 화면으로 넘어가는 게 너무 오래 걸려」) — 3천 건 목록을 한 장씩 memo · 마우스 올림은 CSS(:hover)로
 *  고른 카드가 바뀌면 그 두 장만 다시 그린다 · content-visibility 로 화면 밖 카드는 그리지 않는다 */
interface CardActs { select: (id: string) => void; dup: (g: GovProgram) => void; edit: (g: GovProgram) => void; restore: (g: GovProgram) => void; del: (g: GovProgram) => void }
const CARD_CSS = '.gov-card{content-visibility:auto;contain-intrinsic-size:auto 76px}.gov-card .gov-card-acts{display:none}.gov-card:hover .gov-card-acts{display:flex}@media (hover:none){.gov-card.sel .gov-card-acts{display:flex}}';
const cardBtn = { background:C.white, border:`1px solid ${C.border}`, borderRadius:R.sm, padding:'2px 6px', cursor:'pointer', fontSize:T.xs, color:C.sub } as const;
const GovProgCard = memo(function GovProgCard({ g, sel, kidsN, grpN, acts }: { g: GovProgram; sel: boolean; kidsN: number; grpN: number; acts: CardActs }) {
  const inactive = g.is_active === false;
  const hk = g.hidden_kind && !g.hide_off ? HIDDEN_KIND_LABEL[g.hidden_kind] : null;
  return (
    <div data-prog={g.id} className={`gov-card${sel ? ' sel' : ''}`} onClick={() => acts.select(g.id)}
      style={{ padding:'10px 12px', borderRadius:R.md, marginBottom:6, cursor:'pointer', position:'relative', background: sel ? C.accentLt : C.white, border:`1px solid ${sel ? C.accent : C.border}`, opacity: inactive ? 0.6 : 1 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:6 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:600, color:C.text, marginBottom:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:5 }}>
            <span style={{ overflow:'hidden', textOverflow:'ellipsis' }}>{g.name}</span>
            {inactive && <Badge bg={C.bgAlt} color={C.muted} style={{ flexShrink:0 }}>비활성</Badge>}
          </div>
          <div style={{ fontSize:11, color:C.sub }}>{g.agency||'-'} · {g.amount||'-'}</div>
          {g.category && <div style={{ fontSize:11, color:C.sub, marginTop:2 }}>{g.category}</div>}
          {(kidsN > 0 || grpN > 1 || hk || g.dup_review || g.dup_off) && (   // 같은 공고 묶음 · 같은 사업 묶음 · 가린 공고 · 확인 꼬리표 (2026-10-05 · 10-07)
            <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:4 }}>
              {kidsN > 0 && <Badge bg={C.bgAlt} color={C.sub} style={{ fontWeight:600 }}>같은 공고 {kidsN + 1}건</Badge>}
              {grpN > 1 && <Badge bg="#FAEEDA" color="#633806" style={{ fontWeight:600 }}>같은 사업 {grpN}건{g.prog_part ? ` · ${g.prog_part}` : ''}</Badge>}
              {hk && <Badge bg={isHiddenKind(g) ? C.bgAlt : '#FFFBEB'} color={isHiddenKind(g) ? C.muted : '#B45309'} style={{ fontWeight:600 }}>{hk.label}</Badge>}
              {g.dup_review && <Badge bg="#FFFBEB" color="#B45309" style={{ fontWeight:600 }}>같은 공고일 수 있음</Badge>}
              {g.dup_off && <Badge bg={C.bgAlt} color={C.muted}>따로 보기</Badge>}
            </div>
          )}
        </div>
        {!inactive && <DDayBadge deadline={g.deadline} />}
      </div>
      <div className="gov-card-acts" style={{ position:'absolute', top:6, right:6, gap:3 }}>
        {!inactive && <button onClick={e => { e.stopPropagation(); acts.dup(g); }} style={cardBtn}>복제</button>}
        <button onClick={e => { e.stopPropagation(); acts.edit(g); }} style={cardBtn}>수정</button>
        {inactive
          ? <button onClick={e => { e.stopPropagation(); acts.restore(g); }} style={{ ...cardBtn, border:`1px solid ${C.green}44`, color:C.green }}>복원</button>
          : <button onClick={e => { e.stopPropagation(); acts.del(g); }} style={{ ...cardBtn, border:`1px solid ${C.red}44`, color:C.red }}>삭제</button>}
      </div>
    </div>
  );
});

export default function GovPage({ store, senderName }: Props) {
  const { companies, fps } = store;
  const sender = senderName || '관리자';
  const [search, setSearch] = useState('');
  const [catFs, setCatFs] = useState<Set<string>>(new Set()); // 분류 복수선택 — 빈 Set = 전체 (사용자 확정)

  const [regionF, setRegionF] = useState('전체');
  const [deadlineMonth, setDeadlineMonth] = useState('전체');
  const [showModal, setShowModal] = useState(false);
  const [editProg, setEditProg] = useState<string | null>(null);
  const [selProgId, setSelProgId] = useState<string | null>(null);
  const [toast, setToast] = useState('');
  const [form, setForm] = useState<GovProgramFormState>(emptyGovProgramForm());
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [showSmsPreview, setShowSmsPreview] = useState(false);
  const [smsTarget, setSmsTarget] = useState<'fp'|'company'|null>(null);
  const [smsSending, setSmsSending] = useState(false);
  const [sendPhase, setSendPhase] = useState<'preview'|'done'>('preview');
  const [sendResults, setSendResults] = useState<Record<string, { ok: boolean; error?: string }>>({});
  const [dupWarning, setDupWarning] = useState<{ name: string; agency: string } | null>(null);
  const [view, setView] = useState<GovView>('list');
  const [feedNew, setFeedNew] = useState<number | null>(null);
  useEffect(() => { api.getGovFeedStats().then(r => setFeedNew(r.data?.counts?.new ?? null)).catch(() => {}); }, []);
  // 전용 대상 자동 체크 검토 (2026-10-05f) — 「등록된 공고」 안의 화면 · 검토 대기 건수
  const [targetsOpen, setTargetsOpen] = useState(false);
  const [targetsN, setTargetsN] = useState<number | null>(null);
  // 「AI 다시 읽기」(2026-10-05o) — 공고문 전체를 AI 가 읽어 하위 업종 · 전용 대상을 다시 판정
  const [aiReading, setAiReading] = useState<string | null>(null);
  const rereadTargets = useCallback(async (id: string) => {
    if (aiReading) return;
    setAiReading(id);
    try {
      const r = await api.aiRereadGovTarget(id);
      if (!r.success || !r.data) throw new Error(r.error || 'AI 다시 읽기에 실패했습니다');
      if ('background' in r.data) { setToast('AI 가 아직 읽는 중 — 잠시 뒤 목록을 새로고침하면 결과가 보입니다'); return; }
      const d = r.data;
      const on = [...(d.row.subs || []), ...d.row.sec];
      setToast(`AI 다시 읽기 — ${on.length ? on.map(targetText).join(' · ') : '하위 업종 · 전용 대상 없음'}${d.changed ? ' (바뀜 · 검토 대기)' : ' (그대로)'}`);
      await store.refreshGov();
    } catch (e) { setToast(e instanceof Error ? e.message : 'AI 다시 읽기에 실패했습니다'); }
    finally { setAiReading(null); }
  }, [aiReading, store]);
  const refreshTargetsN = useCallback(() => { api.getGovTargetPendingCount().then(r => setTargetsN(r.data?.pending ?? null)).catch(() => {}); }, []);
  useEffect(() => { refreshTargetsN(); }, [refreshTargetsN, store.govPrograms]);

  // 목록 「비활성 보기」 토글 — 하드 삭제 대신 is_active=false 로 전환된 공고(감사 A-10)
  const [showInactive, setShowInactive] = useState(false);
  const [dupF, setDupF] = useState<DupFilter>('all');          // 「상태」 칸 — 같은 공고 확인 · 묶음 · 가린 공고
  const [dupOpen, setDupOpen] = useState(false);               // 상세 「같은 공고 N건 묶음」 펼침
  const [dupBusy, setDupBusy] = useState<string | null>(null); // 손 조작 중인 공고 id
  const [inactivePrograms, setInactivePrograms] = useState<GovProgram[]>([]);
  const refreshInactive = useCallback(() => {
    api.getGovPrograms({ include_inactive: true })
      .then(r => setInactivePrograms((r.data || []).filter(p => p.is_active === false)))
      .catch(() => {});
  }, []);
  useEffect(() => { if (showInactive) refreshInactive(); else setInactivePrograms([]); }, [showInactive, refreshInactive]);
  const allPrograms = useMemo(() => showInactive ? [...store.govPrograms, ...inactivePrograms] : store.govPrograms, [store.govPrograms, inactivePrograms, showInactive]);
  // 같은 공고 묶음은 대표 한 줄만 (2026-10-05 · 148) — 식구는 상세의 「같은 공고 N건 묶음」에서 펼쳐 본다 · 검색은 식구 이름도 맞춘다
  const dupFold = useMemo(() => foldDupGroups(allPrograms), [allPrograms]);
  const govPrograms = dupFold.visible;
  // 같은 사업 묶음(prog_group · 2026-10-07) — 식구 수는 접힌 식구(같은 공고)까지 센다 · 카드 꼬리표 · 「상태」 칸
  const grpCounts = useMemo(() => progGroupCounts(allPrograms), [allPrograms]);
  const grpNOf = useCallback((g: GovProgram) => (g.prog_group ? grpCounts.get(g.prog_group) || 0 : 0), [grpCounts]);
  const dupCounts = useMemo(() => ({
    review: govPrograms.filter(g => g.dup_review && g.is_active !== false).length,
    grouped: govPrograms.filter(g => dupFold.kidsOf.has(g.id)).length,
    proggrp: govPrograms.filter(g => grpNOf(g) > 1).length,
    hidden: govPrograms.filter(g => isHiddenKind(g)).length,
  }), [govPrograms, dupFold, grpNOf]);
  useEffect(() => { setDupOpen(false); }, [selProgId]);

  useEffect(() => { if(!toast) return; const t=setTimeout(()=>setToast(''),3000); return()=>clearTimeout(t); }, [toast]);
  useEffect(() => { setCheckedIds(new Set()); setShowSmsPreview(false); }, [selProgId]);

  // 지역 칸 (2026-10-05 지도사 「각각 지역에 넣으면 되잖아 — 서울에도 보이고 경기에도 보이고 인천에도 보이게」)
  //  「서울,경기,인천」 공고는 서울·경기·인천 세 칸에 각각 1건 · 지역을 정하지 않은 공고는 「전국」 칸 하나
  //  순서 = 전국 → 서울·경기·인천 → 그 밖 광역(지도 순서) — 검토함 지역 칸 · 「정렬 › 지역」 과 같다
  const regionCounts = useMemo(() => {
    const m = new Map<string,number>();
    govPrograms.forEach(g => {
      const rs = splitRegions(g.region);
      if (rs.length === 0) { m.set(NATIONWIDE, (m.get(NATIONWIDE)||0)+1); return; }
      rs.forEach(r => m.set(r, (m.get(r)||0)+1));
    });
    return m;
  }, [govPrograms]);
  const uniqueRegions = useMemo(() => {
    const has = (r: string) => (regionCounts.get(r)||0) > 0;
    const extra = Array.from(regionCounts.keys()).filter(r => r !== NATIONWIDE && !REGION_ORDER.includes(r)).sort((a, b) => a.localeCompare(b, 'ko')); // 모르는 이름은 맨 뒤
    return ['전체', ...(has(NATIONWIDE) ? [NATIONWIDE] : []), ...REGION_ORDER.filter(has), ...extra];
  }, [regionCounts]);
  // 고른 지역이 목록에서 사라지면(비활성 보기 해제 등) 「전체」 로 — 보이지 않는 조건이 목록을 비우지 않게
  useEffect(() => { if (regionF !== '전체' && !uniqueRegions.includes(regionF)) setRegionF('전체'); }, [uniqueRegions, regionF]);

  const catCounts = useMemo(() => {
    const m = new Map<string,number>();
    m.set('전체', govPrograms.length);
    govPrograms.forEach(g => { (g.category || '기타').split(',').forEach(c => { const ct = canonCat(c.trim() || '기타'); m.set(ct, (m.get(ct)||0)+1); }); });   // 옛 라벨은 새 체계로 집계
    return m;
  }, [govPrograms]);

  const filtered = useMemo(() =>
    govPrograms.filter(g => {
      if (search && !g.name.includes(search) && !(g.agency||'').includes(search) && !(dupFold.kidsOf.get(g.id) || []).some(k => k.name.includes(search) || (k.agency||'').includes(search))) return false;
      if (dupF === 'review' && !g.dup_review) return false;
      if (dupF === 'grouped' && !dupFold.kidsOf.has(g.id)) return false;
      if (dupF === 'proggrp' && grpNOf(g) <= 1) return false;
      if (dupF === 'hidden' && !isHiddenKind(g)) return false;
      if (catFs.size > 0 && !(g.category||'').split(',').some(c => catFs.has(canonCat(c.trim())))) return false;
      if (regionF !== '전체') { // 「전국」 = 지역을 정하지 않은 공고만 · 광역 = 그 지역이 들어 있는 공고(전국 공고는 안 섞음 — 검토함 기본과 같다)
        const rs = splitRegions(g.region);
        if (regionF === NATIONWIDE ? rs.length > 0 : !rs.includes(regionF)) return false;
      }
      if (deadlineMonth !== '전체' && !(g.deadline||'').startsWith(deadlineMonth)) return false;
      return true;
    }).sort((a,b) => { if (!a.deadline) return 1; if (!b.deadline) return -1; return a.deadline.localeCompare(b.deadline); }),
    [govPrograms, search, catFs, regionF, deadlineMonth, dupF, dupFold, grpNOf]
  );
  const [progSort, setProgSort] = useState<ProgSort>(loadProgSort);
  useEffect(() => { saveProgSort(progSort); }, [progSort]);
  const cycleSort = (key: ProgSortKey) => setProgSort(prev =>
    !prev || prev.key !== key ? { key, dir: 'asc' } : prev.dir === 'asc' ? { key, dir: 'desc' } : null);
  const sortedList = useMemo(() => sortPrograms(filtered, progSort), [filtered, progSort]);

  const selProg = useMemo(() => selProgId ? allPrograms.find(g => g.id === selProgId) || null : null, [allPrograms, selProgId]);   // 접힌 식구도 고를 수 있다(상세 묶음에서)

  // ── 매칭: 서버 엔진(services/govMatch)이 엄격 모드(strict:true)로 판정한다 ──
  //   문자 발송 대상 화면이라 「전제로 통과」「세부 업종 넓게 통과」도 near 로 돌린다(지도사 2026-09-25).
  const [matchRes, setMatchRes] = useState<api.GovMatchResult | null>(null);
  const [matchLoading, setMatchLoading] = useState(false);
  useEffect(() => {
    if (!selProgId) { setMatchRes(null); return; }
    let cancelled = false;
    setMatchLoading(true);
    api.getGovMatches(selProgId)
      .then(d => { if (!cancelled) setMatchRes(d.data || null); })
      .catch(() => { if (!cancelled) setMatchRes(null); })
      .finally(() => { if (!cancelled) setMatchLoading(false); });
    return () => { cancelled = true; };
  }, [selProgId, companies]);
  // 공고문 보기 (2026-10-03 「등록된 공고에서는 PDF 를 볼 방법이 없다」) — 이 사업을 만든 검토함 공고의 원문(PDF·HWP)을 오른쪽 창으로
  const [docSrc, setDocSrc] = useState<{ progId: string; groupId: string | null } | null>(null);
  const [docOpen, setDocOpen] = useState(false);
  useEffect(() => {
    setDocOpen(false);
    if (!selProgId) { setDocSrc(null); return; }
    let cancelled = false;
    setDocSrc(null);
    api.getGovFeedByProgram(selProgId)
      .then(r => { if (!cancelled) setDocSrc({ progId: selProgId, groupId: r.data?.groupId ?? null }); })
      .catch(() => { if (!cancelled) setDocSrc({ progId: selProgId, groupId: null }); });
    return () => { cancelled = true; };
  }, [selProgId]);
  useEffect(() => {
    if (!docOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDocOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [docOpen]);
  const docGroupId = docSrc && docSrc.progId === selProgId ? docSrc.groupId : null;
  const matchedCompanies = useMemo<api.GovMatchCompany[]>(() => matchRes?.pass ?? [], [matchRes]);
  const nearCompanies = useMemo<api.GovMatchCompany[]>(() => matchRes?.near ?? [], [matchRes]);

  function getFp(c: api.GovMatchCompany): FP | undefined { return c.fps?.[0] ? fps.find(f => f.id === c.fps[0].fp_id) : undefined; }
  function toggleCheck(id: string) { setCheckedIds(prev => { const n = new Set(prev); if(n.has(id)) n.delete(id); else n.add(id); return n; }); }
  function toggleAll() { if(checkedIds.size===matchedCompanies.length) setCheckedIds(new Set()); else setCheckedIds(new Set(matchedCompanies.map(c=>c.id))); }

  function groupByFp(cos: api.GovMatchCompany[]): { fp: FP; companies: api.GovMatchCompany[] }[] {
    const m = new Map<string, { fp: FP; companies: api.GovMatchCompany[] }>();
    for (const c of cos) { const fp = getFp(c); if (!fp?.phone) continue; const e = m.get(fp.id); if (e) e.companies.push(c); else m.set(fp.id, { fp, companies: [c] }); }
    return Array.from(m.values());
  }

  // 추천 문자 (2026-10-04 지도사 「깔끔하게」) — 제목 한 줄 + ■ 항목별 줄바꿈 · 빈 항목은 뺀다 · ■▶ 는 문자(EUC-KR)에서도 깨지지 않는 기호
  function recoBody(prog: GovProgram): string {
    const deadlineStr = govPeriodSms(prog);   // 신청기한 한 줄 — 업체 상세 추천 카드(GovRecoList)와 같은 규칙(govPeriod.ts)
    const cat = String(prog.category || '').split(/\s*,\s*/).filter(Boolean).join(', ');
    const rows: [string, string][] = [['사업명', prog.name], ['분류', cat], ['지원금액', String(prog.amount || '').trim()], ['신청기한', deadlineStr]];
    const link = prog.short_url || prog.link || '';
    return rows.filter(([, v]) => v).map(([k, v]) => `■ ${k}\n${v}`).join('\n\n') + (link ? `\n\n▶ 공고 보기\n${link}` : '');
  }
  function buildSms(fp: FP, cos: api.GovMatchCompany[], prog: GovProgram): string {
    const names = cos.map(c => `${c.name}(${c.ceo || '대표'})`).join(', ');
    return `[${sender}] ${names} 대표님에게 추천하는 지원사업 정보\n\n${recoBody(prog)}`;
  }

  function buildCompanySms(c: api.GovMatchCompany, prog: GovProgram): string {
    return `[${sender}] ${c.name} ${ceoContact(c).name} 대표님께 추천드리는 지원사업 정보\n\n${recoBody(prog)}`.replace('  대표님', ' 대표님');
  }

  function groupCompanyDirect(cos: api.GovMatchCompany[]): { phone: string; name: string; message: string }[] {
    if (!selProg) return [];
    // 받는 사람 = 실제 대표(있으면 · 2026-10-18 지도사 「문자 받는 사람은 실제 대표」)
    return cos.filter(c => ceoContact(c).phone).map(c => ({
      phone: ceoContact(c).phone,
      name: `${c.name} (${ceoContact(c).name || '대표'})`,
      message: buildCompanySms(c, selProg!),
    }));
  }

  // 미리보기·발송·재발송이 같은 대상 목록을 쓴다 — checkedIds 없으면 pass 전체
  const sendItems = useMemo<SendItem[]>(() => {
    if (!selProg || !smsTarget) return [];
    const checked = matchedCompanies.filter(c => checkedIds.has(c.id));
    const target = checked.length > 0 ? checked : matchedCompanies;
    if (smsTarget === 'company') {
      return groupCompanyDirect(target).map(m => ({ key: m.phone, label: m.name, phone: m.phone, message: m.message }));
    }
    return groupByFp(target).map(({ fp, companies: cos }) => ({
      key: fp.id, label: `${fp.name} FP (${fp.phone})`, phone: fp.phone, message: buildSms(fp, cos, selProg), count: cos.length,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selProg, smsTarget, matchedCompanies, checkedIds, sender]);

  async function doSend(items: SendItem[]) {
    if (items.length === 0) return;
    setSmsSending(true);
    const results: Record<string, { ok: boolean; error?: string }> = {};
    for (const item of items) {
      try {
        const r = await api.sendSms({ to: item.phone, message: item.message, context: 'tm-gov' });
        results[item.key] = r.data?.sent ? { ok: true } : { ok: false, error: r.data?.error || '발송 실패' };
      } catch (e) {
        results[item.key] = { ok: false, error: e instanceof Error ? e.message : '발송 실패' };
      }
    }
    setSendResults(prev => ({ ...prev, ...results }));
    setSmsSending(false); setSendPhase('done');
    const sent = Object.values(results).filter(r => r.ok).length;
    const fail = Object.values(results).filter(r => !r.ok).length;
    setToast(`SMS ${sent}건 발송${fail > 0 ? `, ${fail}건 실패 — 사유는 미리보기에서 확인` : ' 완료'}`);
  }
  function handleSmsSend() { void doSend(sendItems); }
  function handleResendFailed() { void doSend(sendItems.filter(i => sendResults[i.key] && !sendResults[i.key].ok)); }
  function openSmsPreview(target: 'fp'|'company') { setSmsTarget(target); setSendPhase('preview'); setSendResults({}); setShowSmsPreview(true); }
  function closeSmsPreview() { setShowSmsPreview(false); setSmsTarget(null); setSendPhase('preview'); setSendResults({}); }

  function openAdd() { setEditProg(null); setForm(emptyGovProgramForm()); setShowModal(true); }
  function openEdit(g: GovProgram) { setEditProg(g.id); setForm(govProgramFormFromRow(g)); setShowModal(true); }
  /** 전년도 공고 복제 — 마감일·신청시작일만 +1년, 새 등록으로 저장(지도사 확정) */
  function openDuplicate(g: GovProgram) { setEditProg(null); setForm(govProgramFormFromRow(g, { shiftYear: true })); setShowModal(true); }

  async function handleSave() {
    if (!canSaveGovForm(form)) return; // 지난 사업은 리포트 사업명만 있어도 된다 (2026-10-01)
    try {
      const payload = govProgramFormToPayload(form);
      if (editProg) {
        const res = await api.updateGovProgram(editProg, payload);
        setToast('수정 완료');
        if (res.warning === 'duplicate' && res.data) setDupWarning({ name: res.data.name, agency: res.data.agency || '' });
      } else {
        const res = await api.createGovProgram(payload);
        setToast('등록 완료');
        if (res.data) setSelProgId(res.data.id); // 등록 직후 매칭 결과가 바로 보이게
        if (res.warning === 'duplicate' && res.data) setDupWarning({ name: res.data.name, agency: res.data.agency || '' });
      }
      await store.refreshGov(); setShowModal(false); setEditProg(null);
    } catch (e: unknown) { setToast(e instanceof Error ? e.message : '저장 실패'); }
  }
  async function handleDel(g: GovProgram) {
    if (!window.confirm('비활성화하시겠습니까? 목록에서 숨겨지며 "비활성 보기"에서 되돌릴 수 있습니다.')) return;
    try {
      await api.deleteGovProgram(g.id);
      if (selProgId===g.id) setSelProgId(null);
      await store.refreshGov(); if (showInactive) refreshInactive();
      setToast('비활성화됨');
    } catch(e: unknown) { setToast(e instanceof Error ? e.message : '처리 실패'); }
  }
  async function handleRestore(g: GovProgram) {
    try { await api.updateGovProgram(g.id, { is_active: true }); await store.refreshGov(); refreshInactive(); setToast('복원됨'); }
    catch (e: unknown) { setToast(e instanceof Error ? e.message : '복원 실패'); }
  }

  // 같은 공고 묶음 손 조작 (2026-10-05) — 서버가 다시 묶고 목록을 새로 읽는다
  async function dupAction(g: GovProgram, body: { dup_off?: boolean; hide_off?: boolean; pin?: string | null }, done: string) {
    setDupBusy(g.id);
    try {
      const r = await api.setGovDup(g.id, body);
      if (!r.success) { setToast(r.error || '처리에 실패했습니다'); return; }
      await store.refreshGov();
      if (showInactive) refreshInactive();
      setToast(done);
    } catch (e) { setToast(e instanceof Error ? e.message : '처리에 실패했습니다'); }
    finally { setDupBusy(null); }
  }

  // 「등록된 공고 | 공고 검토함」 전환 (2026-09-30) — 검토함 숫자 = 검토 대기(stats.counts.new)
  const viewSwitch = <GovViewSwitch view={view} onChange={v => { setTargetsOpen(false); setView(v); }} registered={store.govPrograms.length} inbox={feedNew} />;
  // 카드 동작 — 한 번만 만든다(memo 카드가 다시 그려지지 않게) · 최신 함수는 ref 로
  const actsRef = useRef<CardActs | null>(null);
  actsRef.current = { select: (id) => setSelProgId(cur => cur === id ? null : id), dup: openDuplicate, edit: openEdit, restore: g => { void handleRestore(g); }, del: g => { void handleDel(g); } };
  const cardActs = useMemo<CardActs>(() => ({
    select: id => actsRef.current?.select(id), dup: g => actsRef.current?.dup(g), edit: g => actsRef.current?.edit(g),
    restore: g => actsRef.current?.restore(g), del: g => actsRef.current?.del(g),
  }), []);
  // 「공고 검토함」에서 연 공고 — 목록에서 그 카드가 보이게
  const [scrollTo, setScrollTo] = useState<string | null>(null);
  // 2026-10-08 fphub 리포트 로드맵 줄 → 이 공고의 검토함 상세([원문 | 조건])로 — 검토함 공고가 없으면(직접 등록) 등록된 공고 카드로
  const [deepGroup, setDeepGroup] = useState<string | null>(null);
  useEffect(() => {
    let id: string | null = null;
    try { id = sessionStorage.getItem('tm_gov_prog'); sessionStorage.removeItem('tm_gov_prog'); } catch { id = null; }
    if (!id) return;
    const progId = id;
    let cancelled = false;
    api.getGovFeedByProgram(progId)
      .then(r => {
        if (cancelled) return;
        const gid = r.data?.groupId ?? null;
        if (gid) { setTargetsOpen(false); setDeepGroup(gid); setView('inbox'); }
        else { setSelProgId(progId); setScrollTo(progId); }
      })
      .catch(() => { if (!cancelled) { setSelProgId(progId); setScrollTo(progId); setToast('검토함 공고를 찾지 못해 등록된 공고로 열었습니다'); } });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (!scrollTo) return;
    const raf = requestAnimationFrame(() => { document.querySelector(`[data-prog="${CSS.escape(scrollTo)}"]`)?.scrollIntoView({ block: 'center' }); setScrollTo(null); });
    return () => cancelAnimationFrame(raf);
  }, [scrollTo]);

  if (view === 'inbox') return <GovFeedInbox store={store} topLeft={viewSwitch} onCounts={setFeedNew} openGroupId={deepGroup} />;
  if (view === 'list' && targetsOpen) return (
    <GovTargetReview topLeft={viewSwitch} onBack={() => { setTargetsOpen(false); void store.refreshGov(); }} onCount={setTargetsN}
      onOpenProgram={id => { setTargetsOpen(false); setSelProgId(id); setScrollTo(id); void store.refreshGov(); }} />
  );
  if (view === 'share') return <GovSharePage topLeft={viewSwitch} onGoInbox={() => setView('inbox')} />;
  if (view === 'check') return <GovTargetCheckPage topLeft={viewSwitch} />;

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
    <GovTopBar left={viewSwitch} />
    <div style={{ display:'flex', flex:1, minHeight:0 }}>
      {/* ═══ COL 1: 필터 ═══ */}
      <div style={{ width:160, flexShrink:0, borderRight:`1px solid ${C.border}`, overflowY:'auto', padding:'14px 12px' }}>
        <div style={secTitle}>분류</div>
        <div style={{ marginBottom:16 }}>
          {[...GOV_CATEGORIES, ...GOV_ARTS].filter(c => c === '전체' || (catCounts.get(c)||0) > 0).map(c => (
            <div key={c}
              onClick={() => setCatFs(prev => { // '전체' = 해제, 나머지는 토글 (복수선택)
                if (c === '전체') return new Set();
                const n = new Set(prev);
                if (n.has(c)) n.delete(c); else n.add(c);
                return n;
              })}
              style={filterItem(c === '전체' ? catFs.size === 0 : catFs.has(c))}>
              <span>{c === '전체' ? '전체' : (catFs.has(c) ? '✓ ' : '') + c}</span>
              {(catCounts.get(c)||0) > 0 && <span style={{ fontSize:11, color: (c === '전체' ? catFs.size === 0 : catFs.has(c)) ? C.accent : C.muted, fontWeight:600 }}>{catCounts.get(c)}</span>}
            </div>
          ))}
        </div>

        <div style={secTitle}>신청연월</div>
        <div style={{ marginBottom:16 }}>
          {(() => {
            const months = new Map<string,number>();
            months.set('전체', govPrograms.filter(g => g.deadline).length);
            govPrograms.forEach(g => {
              if (!g.deadline) return;
              const ym = g.deadline.slice(0, 7);
              months.set(ym, (months.get(ym)||0)+1);
            });
            const sortedMonths = ['전체', ...Array.from(months.keys()).filter(k => k !== '전체').sort()];
            return sortedMonths.filter(m => m === '전체' || (months.get(m)||0) > 0).map(m => (
              <div key={m} onClick={() => setDeadlineMonth(m)} style={filterItem(deadlineMonth===m)}>
                <span>{m === '전체' ? '전체' : m.slice(2).replace('-', '년 ') + '월'}</span>
                {(months.get(m)||0) > 0 && <span style={{ fontSize:11, color: deadlineMonth===m ? C.accent : C.muted, fontWeight:600 }}>{months.get(m)}</span>}
              </div>
            ));
          })()}
        </div>
        <div style={secTitle}>지역</div>
        {/* 검토함 지역 칸과 같은 2열 — 전체는 한 줄 · 전국 → 서울·경기·인천 → 그 밖 광역 · 건수 0인 지역은 숨김(다른 칸과 같다) */}
        <div style={{ marginBottom:16, display:'grid', gridTemplateColumns:'1fr 1fr', gap:2 }}>
          {uniqueRegions.map(r => (
            <div key={r} onClick={() => setRegionF(r)}
              style={{ ...filterItem(regionF===r), ...(r === '전체' ? { gridColumn:'1 / -1', padding:'5px 8px' } : { padding:'5px 6px', gap:2 }) }}
              title={r === '전체' ? '모든 공고' : r === NATIONWIDE ? '지역을 정하지 않은 공고' : `${r} 공고 — 여러 지역 공고는 각 지역에 모두 보입니다`}>
              <span>{r}</span>
              {r !== '전체' && (regionCounts.get(r)||0) > 0 && <span style={{ fontSize:11, color: regionF===r ? C.accent : C.muted, fontWeight:600, fontVariantNumeric:'tabular-nums' }}>{regionCounts.get(r)}</span>}
            </div>
          ))}
        </div>
        {/* 같은 공고 묶음 · 상품 아닌 공고 (2026-10-05 · 148) — 확인할 것이 있을 때만 숫자가 붙는다 */}
        <div style={secTitle}>상태</div>
        <div style={{ marginBottom:16 }}>
          {([['all', '전체', 0, ''], ['review', '같은 공고 확인', dupCounts.review, '같은 공고일 수 있는데 확신이 모자라 따로 등록된 공고 — 「묶기」 또는 「같은 공고 아님」으로 정리'], ['grouped', '묶음 있음', dupCounts.grouped, '같은 공고가 여러 건 묶인 대표 공고'], ['proggrp', '같은 사업 묶음', dupCounts.proggrp, '운영기관 · 차수 · 유형만 다른 공고를 한 사업으로 묶은 것 — 리포트에는 한 줄로 나갑니다 (상세에서 풀기 · 묶기)'], ['hidden', '가린 공고', dupCounts.hidden, '기관 모집 · 통합 공고 — 매칭 · 리포트 · 단톡방에서 빠집니다']] as [DupFilter, string, number, string][])
            .filter(([k, , n]) => k === 'all' || n > 0 || dupF === k).map(([k, label, n, title]) => (
            <div key={k} onClick={() => setDupF(k)} style={filterItem(dupF === k)} title={title || undefined}>
              <span>{label}</span>
              {k !== 'all' && n > 0 && <span style={{ fontSize:11, color: dupF === k ? C.accent : k === 'review' ? '#B45309' : C.muted, fontWeight:600, fontVariantNumeric:'tabular-nums' }}>{n}</span>}
            </div>
          ))}
        </div>
        <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:C.muted, cursor:'pointer' }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} style={{ accentColor:C.accent }} />
          비활성 보기{inactivePrograms.length > 0 ? ` (${inactivePrograms.length})` : ''}
        </label>
      </div>

      {/* ═══ COL 2: 목록 ═══ */}
      <div style={{ width:280, flexShrink:0, borderRight:`1px solid ${C.border}`, overflowY:'auto', padding:'12px' }}>
        {/* 전용 대상 자동 체크 검토 (2026-10-05f) — 사업명·본문에서 자동으로 켠 업종 전용·특수 자격을 한 번 더 추린다 */}
        <button type="button" onClick={() => setTargetsOpen(true)} title="사업명·본문에서 자동으로 켠 전용 대상(업종 전용·특수 자격)을 근거와 함께 보고 추립니다"
          style={{ display:'flex', alignItems:'center', gap:8, width:'100%', marginBottom:10, padding:'8px 10px', borderRadius:R.md, cursor:'pointer', textAlign:'left', fontFamily:'inherit',
            border:`1px solid ${targetsN ? '#FDE68A' : C.border}`, background: targetsN ? '#FFFBEB' : C.bgAlt }}>
          <span style={{ flex:1, minWidth:0 }}>
            <span style={{ display:'block', fontSize:T.sm, fontWeight:700, color: targetsN ? '#92400E' : C.sub }}>하위 업종 · 전용 대상 검토</span>
            <span style={{ display:'block', fontSize:T.xs, color: targetsN ? '#B45309' : C.muted, marginTop:1 }}>{targetsN ? '자동으로 켠 값을 한 번 더 추려 주세요' : targetsN === 0 ? '검토할 것 없음 · 완료 목록 보기' : '자동 체크 · 옛 분류 옮김 검토'}</span>
          </span>
          {targetsN != null && targetsN > 0 && <span style={{ flexShrink:0, minWidth:22, padding:'1px 7px', borderRadius:999, background:'#F59E0B', color:C.white, fontSize:T.xs, fontWeight:700, textAlign:'center', fontVariantNumeric:'tabular-nums' }}>{targetsN.toLocaleString()}</span>}
          <span style={{ flexShrink:0, color: targetsN ? '#B45309' : C.muted, fontSize:T.sm }}>›</span>
        </button>
        <div style={{ display:'flex', gap:6, marginBottom:10 }}>
          <SearchInput value={search} onChange={setSearch} placeholder="사업명·기관명" style={{ flex:1 }} />
          <Btn onClick={openAdd} style={{ fontSize:T.xs, padding:'6px 10px', whiteSpace:'nowrap' }}>+등록</Btn>
        </div>
        {/* 정렬 — 한 번 오름차순 ▲ · 두 번 내림차순 ▼ · 세 번 기본(마감 임박순) */}
        <div style={{ display:'flex', alignItems:'center', gap:3, marginBottom:10, flexWrap:'wrap' }}>
          <span style={{ fontSize:T.xs, color:C.muted, marginRight:3 }}>정렬</span>
          {PROG_SORT_COLS.map(col => {
            const on = progSort?.key === col.key;
            return (
              <button key={col.key} type="button" onClick={() => cycleSort(col.key)}
                title={`${col.title} — ${!on ? '누르면 오름차순' : progSort?.dir === 'asc' ? '누르면 내림차순' : '누르면 기본 순서'}`}
                aria-pressed={on}
                style={{ display:'inline-flex', alignItems:'center', gap:2, padding:'3px 6px', borderRadius:R.sm, cursor:'pointer', fontSize:T.xs, lineHeight:1.3,
                  border:`1px solid ${on ? C.accent + '66' : C.border}`, background: on ? C.accentLt : C.white,
                  color: on ? C.accent : C.sub, fontWeight: on ? 600 : 400 }}>
                {col.label}
                {on && <span style={{ fontSize:8, color:C.accent }}>{progSort?.dir === 'asc' ? '▲' : '▼'}</span>}
              </button>
            );
          })}
        </div>
        {sortedList.length === 0 && <Empty icon="🏛️" message="등록된 사업 없음" />}
        <style>{CARD_CSS}</style>
        {sortedList.map(g => <GovProgCard key={g.id} g={g} sel={selProgId === g.id} kidsN={dupFold.kidsOf.get(g.id)?.length ?? 0} grpN={grpNOf(g)} acts={cardActs} />)}
      </div>

      {/* ═══ COL 3: 상세 ═══ */}
      <div style={{ flex:1, overflowY:'auto', padding:'16px 20px' }}>
        {!selProg ? (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', color:C.muted, fontSize:T.md }}>← 사업을 선택하세요</div>
        ) : (() => {
          const inactive = selProg.is_active === false;
          return (
            <div>
              <div style={{ marginBottom:20 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                  <div style={{ fontSize:T.md, fontWeight:700, color:C.text }}>{selProg.name}{inactive && <Badge bg={C.bgAlt} color={C.muted} style={{ marginLeft:8 }}>비활성</Badge>}</div>
                  <div style={{ display:'flex', gap:6 }}>
                    <button onClick={() => openDuplicate(selProg)} style={{ padding:'3px 8px', borderRadius:R.sm, border:`1px solid ${C.border}`, background:C.white, cursor:'pointer', fontSize:T.xs, color:C.sub }}>전년도 복제</button>
                    <button onClick={() => openEdit(selProg)} style={{ padding:'3px 8px', borderRadius:R.sm, border:`1px solid ${C.border}`, background:C.white, cursor:'pointer', fontSize:T.xs, color:C.sub }}>수정</button>
                    {inactive
                      ? <button onClick={() => handleRestore(selProg)} style={{ padding:'3px 8px', borderRadius:R.sm, border:`1px solid ${C.green}44`, background:C.greenLt, cursor:'pointer', fontSize:T.xs, color:C.green }}>복원</button>
                      : <button onClick={() => handleDel(selProg)} style={{ padding:'3px 8px', borderRadius:R.sm, border:`1px solid ${C.red}44`, background:C.redLt, cursor:'pointer', fontSize:T.xs, color:C.red }}>삭제</button>}
                  </div>
                </div>
                {/* 리포트 사업명 (2026-10-01) — fphub 리포트에 보이는 공식 사업명. FP 사업명과 다를 때만 */}
                {selProg.report_name && selProg.report_name.trim() !== selProg.name.trim() && (
                  <div style={{ fontSize:T.xs, color:C.sub, marginTop:4 }}><span style={{ fontWeight:600, color:C.muted }}>리포트</span> {selProg.report_name}</div>
                )}
                <div style={{ fontSize:T.sm, color:C.sub, marginTop:4 }}>{[selProg.agency||null, selProg.amount||null, selProg.category||null, (()=>{const rd=(selProg.region_detail||'').trim();const r=(selProg.region||'').trim();return rd?`${r?r+' ':''}${rd}`:(r||null);})()].filter(Boolean).join(' · ')}</div>
                {/* 리포트 짧은 이름 · 대표 주관기관 (2026-10-07) — AI 가 채우고 수정에서 고칠 수 있다 */}
                {(selProg.brief_name || selProg.lead_agency) && (
                  <div style={{ fontSize:T.xs, color:C.sub, marginTop:4 }}><span style={{ fontWeight:600, color:C.muted }}>짧은 이름</span> {selProg.brief_name || '-'}{selProg.lead_agency ? <> · <span style={{ fontWeight:600, color:C.muted }}>주관</span> {selProg.lead_agency}</> : null}{selProg.brief_by === 'manual' ? <span style={{ color:C.muted }}> (직접)</span> : null}</div>
                )}
                {/* 업종 (2026-10-05s 지도사 「여기도 왜 하나만 나오냐」 — 수정에서 고른 업종이 상세에 안 보였다) */}
                {(() => {
                  const raw = (selProg as { target_biz_types?: unknown }).target_biz_types;
                  const biz = Array.isArray(raw) ? raw.map(String) : typeof raw === 'string' && raw ? (() => { try { const j: unknown = JSON.parse(raw); return Array.isArray(j) ? j.map(String) : [raw]; } catch { return raw.split(','); } })() : [];
                  return (
                    <div style={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:4, marginTop:6 }}>
                      <span style={{ fontSize:T.xs, fontWeight:600, color:C.muted, marginRight:2 }}>업종</span>
                      {biz.length ? biz.map(b => <Badge key={b} bg={C.bgAlt} color={C.text} style={{ fontWeight:600 }}>{b}</Badge>) : <span style={{ fontSize:T.xs, color:C.muted }}>무관</span>}
                    </div>
                  );
                })()}
                {/* 하위 업종 · 전용 대상 (2026-10-05f · g) — 켠 하위 업종 · 특수 자격 · 자동 체크 검토 대기면 표시 */}
                {(() => {
                  const cnd = conditionsFromRow(selProg.conditions);
                  const off = Array.isArray(cnd?.sec_off) ? cnd!.sec_off! : [];
                  const sec = [...(Array.isArray(cnd?.subs) ? cnd!.subs! : []), ...(Array.isArray(cnd?.sec) ? cnd!.sec! : [])].filter(k => !off.includes(k));
                  const pend = !!cnd?.sec_auto && cnd.sec_auto.reviewed === false;
                  if (!sec.length && !pend) return null;
                  return (
                    <div style={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:4, marginTop:6 }}>
                      <span style={{ fontSize:T.xs, fontWeight:600, color:C.muted, marginRight:2 }}>세부업종 · 전용 대상</span>
                      {sec.map(k => <TargetChip key={k} k={k} on size="sm" />)}
                      {pend && <button type="button" onClick={() => setTargetsOpen(true)} style={{ border:'none', background:'none', padding:0, cursor:'pointer', fontSize:T.xs, fontWeight:600, color:'#B45309', fontFamily:'inherit' }}>자동 체크 · 검토 대기 ›</button>}
                    </div>
                  );
                })()}
                {/* 세부 유형 (2026-10-05) — 이름·금액만 한 줄 (조건은 수정 화면에서) */}
                {(() => { const tl = tracksSummary(selProg.tracks); return tl ? (
                  <div style={{ fontSize:T.xs, color:C.sub, marginTop:4, lineHeight:1.5 }}><span style={{ fontWeight:600, color:C.muted }}>세부 유형 {selProg.tracks?.length}</span> {tl}</div>
                ) : null; })()}
                {selProg.created_at && (
                  <div style={{ fontSize:T.xs, color:C.muted, marginTop:4 }}>등록일 {fmtDate(selProg.created_at)}</div>
                )}
                {/* 같은 공고 묶음 · 상품 아닌 공고 (2026-10-05 · 148) — 서버가 저장할 때 자동으로 묶고 가린다 · 여기서 지도사가 바로잡는다 */}
                {(() => {
                  const busy = dupBusy === selProg.id;
                  const kids = dupFold.kidsOf.get(selProg.id) || [];
                  const repId = dupFold.repOf.get(selProg.id) || null;
                  const rep = repId ? allPrograms.find(x => x.id === repId) || null : null;
                  const hk = selProg.hidden_kind ? HIDDEN_KIND_LABEL[selProg.hidden_kind] : null;
                  const near = selProg.dup_review && selProg.dup_review_of ? allPrograms.find(x => x.id === selProg.dup_review_of) || null : null;
                  const line = (x: GovProgram) => [x.agency || null, (() => { const rd = (x.region_detail || '').trim(); const r = (x.region || '').trim(); return rd ? `${r ? r + ' ' : ''}${rd}` : (r || '전국'); })(), x.apply_start || x.deadline ? `${x.apply_start ? fmtDate(x.apply_start) : ''}~${x.deadline ? fmtDate(x.deadline) : ''}` : null].filter(Boolean).join(' · ');
                  const box: React.CSSProperties = { marginTop:10, padding:'8px 10px', borderRadius:R.md, border:`1px solid ${C.border}`, background:C.bgAlt, fontSize:T.xs, color:C.sub, lineHeight:1.5 };
                  const warn: React.CSSProperties = { ...box, border:'1px solid #FDE68A', background:'#FFFBEB', color:'#92400E' };
                  if (!kids.length && !rep && !hk && !near && !selProg.dup_off && !selProg.hide_off) return null;
                  return (
                    <div>
                      {kids.length > 0 && (
                        <div style={box}>
                          <button type="button" onClick={() => setDupOpen(o => !o)} aria-expanded={dupOpen}
                            style={{ display:'flex', alignItems:'center', gap:6, width:'100%', border:'none', background:'none', padding:0, cursor:'pointer', fontFamily:'inherit', textAlign:'left' }}>
                            <span style={{ fontSize:T.xs, fontWeight:700, color:C.text }}>같은 공고 {kids.length + 1}건 묶음</span>
                            <span style={{ fontSize:T.xs, color:C.muted }}>— 목록 · 단톡방 · 리포트에는 이 공고 한 줄만 (링크도 이것)</span>
                            <span style={{ marginLeft:'auto', color:C.muted }}>{dupOpen ? '접기' : '펼치기'}</span>
                          </button>
                          {dupOpen && (
                            <div style={{ marginTop:6, display:'flex', flexDirection:'column', gap:4 }}>
                              {kids.map(k => (
                                <div key={k.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 8px', borderRadius:R.sm, background:C.white, border:`1px solid ${C.border}` }}>
                                  <div style={{ flex:1, minWidth:0 }}>
                                    <div role="button" tabIndex={0} onClick={() => setSelProgId(k.id)} onKeyDown={e => { if (e.key === 'Enter') setSelProgId(k.id); }} title="이 공고 보기"
                                      style={{ fontSize:T.xs, fontWeight:600, color:C.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', cursor:'pointer' }}>{k.name}{k.is_active === false ? ' (비활성)' : ''}</div>
                                    <div style={{ fontSize:T.xs, color:C.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{line(k)}</div>
                                  </div>
                                  <button type="button" disabled={dupBusy === k.id} onClick={() => void dupAction(k, { dup_off: true }, '묶음에서 뺐습니다 — 따로 보입니다')} title="이 공고는 같은 공고가 아닙니다 — 묶음에서 빼고 따로 봅니다 (다시 묶지 않습니다)"
                                    style={dupBtn(C.sub, C.white, C.border)}>같은 공고 아님</button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {rep && (
                        <div style={box}>
                          <span style={{ fontWeight:700, color:C.text }}>같은 공고 묶음에 들어 있음</span> — 목록 · 단톡방 · 리포트에는 대표{' '}
                          <span role="button" tabIndex={0} onClick={() => setSelProgId(rep.id)} onKeyDown={e => { if (e.key === 'Enter') setSelProgId(rep.id); }} style={{ color:C.accent, fontWeight:600, cursor:'pointer' }}>「{rep.name}」</span> 한 줄만 나갑니다.
                          <div style={{ marginTop:6 }}>
                            <button type="button" disabled={busy} onClick={() => void dupAction(selProg, { dup_off: true }, '묶음에서 뺐습니다 — 따로 보입니다')} style={dupBtn(C.sub, C.white, C.border)}>같은 공고 아님</button>
                          </div>
                        </div>
                      )}
                      {selProg.dup_off && (
                        <div style={box}>
                          <span style={{ fontWeight:700, color:C.text }}>따로 보기</span> — 같은 공고 묶음에서 뺀 공고입니다 (다시 묶지 않습니다).
                          <button type="button" disabled={busy} onClick={() => void dupAction(selProg, { dup_off: false, pin: null }, '다시 묶음 규칙을 적용했습니다')} style={{ ...dupBtn(C.sub, C.white, C.border), marginLeft:8 }}>다시 묶기</button>
                        </div>
                      )}
                      {near && (
                        <div style={warn}>
                          <span style={{ fontWeight:700 }}>같은 공고일 수 있음</span> —{' '}
                          <span role="button" tabIndex={0} onClick={() => setSelProgId(near.id)} onKeyDown={e => { if (e.key === 'Enter') setSelProgId(near.id); }} style={{ fontWeight:600, cursor:'pointer', textDecoration:'underline' }}>「{near.name}」</span>
                          <span style={{ color:'#B45309' }}> ({line(near)})</span>와 닮았지만 확신이 모자라 따로 등록돼 있습니다.
                          <div style={{ display:'flex', gap:6, marginTop:6 }}>
                            <button type="button" disabled={busy} onClick={() => void dupAction(selProg, { pin: near.id }, '같은 공고로 묶었습니다')} title="같은 공고입니다 — 그 묶음에 넣습니다 (목록에는 대표 한 줄만)" style={dupBtn(C.white, C.accent, C.accent)}>묶기</button>
                            <button type="button" disabled={busy} onClick={() => void dupAction(selProg, { dup_off: true }, '다른 공고로 두었습니다')} title="다른 공고입니다 — 확인 표시를 지우고 따로 둡니다" style={dupBtn('#92400E', C.white, '#FDE68A')}>같은 공고 아님</button>
                          </div>
                        </div>
                      )}
                      {selProg.dup_review && !near && (
                        <div style={warn}>
                          <span style={{ fontWeight:700 }}>같은 공고일 수 있음</span> — 닮은 공고가 지금 목록에 없습니다(비활성 · 마감).
                          <button type="button" disabled={busy} onClick={() => void dupAction(selProg, { dup_off: true }, '다른 공고로 두었습니다')} style={{ ...dupBtn('#92400E', C.white, '#FDE68A'), marginLeft:8 }}>같은 공고 아님</button>
                        </div>
                      )}
                      {hk && (
                        <div style={selProg.hide_off || selProg.hidden_kind === 'umbrella_unsplit' ? box : warn}>
                          <span style={{ fontWeight:700 }}>{hk.label}</span> — {hk.title}{selProg.hide_off ? ' (지금은 「보이기」로 상품처럼 봅니다)' : ''}
                          {selProg.hidden_kind !== 'umbrella_unsplit' && (
                            <button type="button" disabled={busy} onClick={() => void dupAction(selProg, { hide_off: !selProg.hide_off }, selProg.hide_off ? '다시 가립니다' : '상품으로 보입니다')} style={{ ...dupBtn(selProg.hide_off ? C.sub : '#92400E', C.white, selProg.hide_off ? C.border : '#FDE68A'), marginLeft:8 }}>
                              {selProg.hide_off ? '다시 가리기' : '보이기'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
                {/* 같은 사업 묶음 (2026-10-07g 「공고 고치기」) — 운영기관 · 차수 · 유형만 다른 «같은 사업» 식구 · 풀기 · 모두 풀기 · 다른 공고와 묶기 (리포트 패널 ④와 같은 서비스) */}
                <GovProgGroupBox programId={selProg.id} programs={allPrograms} onOpen={id => { setSelProgId(id); setScrollTo(id); }}
                  onChanged={async () => { await store.refreshGov(); if (showInactive) refreshInactive(); }} onToast={setToast} />
                {selProg.apply_start && <div style={{ fontSize:12, color:C.sub, marginTop:6 }}>신청 시작 {fmtDate(selProg.apply_start)}{selProg.recur_yearly ? ' · 매년 반복' : ''}</div>}
                {selProg.first_come ? <div style={{ fontSize:12, color:C.accent, fontWeight:600, marginTop:8 }}>선착순</div> : selProg.deadline && <div style={{ fontSize:12, color:C.orange, fontWeight:600, marginTop:8 }}>마감 {fmtDate(selProg.deadline)} <DDayBadge deadline={selProg.deadline} /></div>}
                {(docGroupId || selProg.link || !inactive) && (
                  <div style={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:10, marginTop:8 }}>
                    {!inactive && (
                      <button type="button" disabled={aiReading === selProg.id} onClick={() => void rereadTargets(selProg.id)} title="공고문 전체를 AI 가 다시 읽고 하위 업종 · 전용 대상을 다시 판정합니다 (한 번 호출 · 예산 안)"
                        style={{ display:'inline-flex', alignItems:'center', gap:5, height:28, padding:'0 11px', borderRadius:R.sm, border:`1px solid ${C.border}`, background:C.white, color:C.sub, fontSize:T.xs, fontWeight:700, cursor:'pointer', opacity: aiReading === selProg.id ? 0.6 : 1 }}>
                        {aiReading === selProg.id ? 'AI 읽는 중…' : 'AI 다시 읽기'}
                      </button>
                    )}
                    {docGroupId && (
                      <button type="button" onClick={() => setDocOpen(true)} title="공고문(PDF·HWP)을 오른쪽 창에서 봅니다 (Esc 닫기)"
                        style={{ display:'inline-flex', alignItems:'center', gap:5, height:28, padding:'0 11px', borderRadius:R.sm, border:`1px solid ${C.accent}55`, background:C.accentLt, color:C.accent, fontSize:T.xs, fontWeight:700, cursor:'pointer' }}>
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 1.5h5.5L13 5v9.5H4z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/><path d="M9.5 1.5V5H13M6 8.5h5M6 11h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                        공고문 보기
                      </button>
                    )}
                    {selProg.link && <a href={selProg.link} target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:C.accent, textDecoration:'underline' }}>공고 바로가기 ↗</a>}
                  </div>
                )}
              </div>

              {/* 결측 안내 — API 가 이미 계산해 주는 quality/blockers 를 렌더만 한다(감사 B-7/D-3) */}
              {matchRes && !matchLoading && matchRes.counts.near > 0 && (() => {
                const top = Object.entries(matchRes.blockers).sort((a,b) => b[1]-a[1])[0];
                return (
                  <div style={{ marginBottom:16, padding:'10px 14px', borderRadius:R.md, background:'#FFFBEB', border:'1px solid #FDE68A', fontSize:12, color:'#92400E' }}>
                    ⚠ 확인 필요 {matchRes.counts.near}건{top ? ` — ${top[0]}만 채우면 +${top[1]}건` : ''}
                  </div>
                );
              })()}

              <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:16 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:T.md, fontWeight:700, color:C.text }}>매칭 업체 ({matchLoading ? '…' : matchedCompanies.length})</span>
                    {matchRes && !matchLoading && (
                      <span style={{ fontSize:11, color:C.muted }}>전체 {matchRes.counts.total}곳 중 · 엄격 매칭(발송 기준)</span>
                    )}
                    {matchedCompanies.length > 0 && (
                      <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:T.xs, color:C.muted, cursor:'pointer' }}>
                        <input type="checkbox" checked={checkedIds.size === matchedCompanies.length && matchedCompanies.length > 0} onChange={toggleAll} style={{ accentColor:C.accent }} />
                        전체선택
                      </label>
                    )}
                  </div>
                </div>
                {matchedCompanies.length > 0 && (
                  <div style={{ display:'flex', gap:6, marginBottom:12 }}>
                    <button onClick={() => openSmsPreview('fp')} style={{ flex:1, padding:'10px 12px', borderRadius:R.md, border:'none', background:'linear-gradient(135deg, #111827, #1B3A5C)', color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
                      👥 FP에게 발송 {checkedIds.size > 0 && <Badge bg="rgba(255,255,255,0.2)" color="#fff" style={{ fontSize:10 }}>{checkedIds.size}</Badge>}
                    </button>
                    <button onClick={() => openSmsPreview('company')} style={{ flex:1, padding:'10px 12px', borderRadius:R.md, border:'none', background:'linear-gradient(135deg, #059669, #047857)', color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
                      🏢 회사에 발송 {checkedIds.size > 0 && <Badge bg="rgba(255,255,255,0.2)" color="#fff" style={{ fontSize:10 }}>{checkedIds.size}</Badge>}
                    </button>
                  </div>
                )}
                {matchedCompanies.length === 0 ? (
                  <div style={{ padding:32, textAlign:'center', border:`2px dashed ${C.border}`, borderRadius:R.lg, background:C.bgAlt }}>
                    <div style={{ fontSize:28, marginBottom:8 }}>🏢</div>
                    <div style={{ fontSize:12, color:C.muted }}>매칭 업체가 없습니다</div>
                  </div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {matchedCompanies.map(c => {
                      const fp = getFp(c);
                      const isChecked = checkedIds.has(c.id);
                      return (
                        <div key={c.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', borderRadius:R.md, border:`1.5px solid ${isChecked ? C.accent : C.border}`, background: isChecked ? C.accentLt : C.white, cursor:'pointer' }} onClick={() => toggleCheck(c.id)}>
                          <input type="checkbox" checked={isChecked} onChange={() => {}} style={{ accentColor:C.accent, flexShrink:0 }} />
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:13, fontWeight:600 }}>{c.name}</div>
                            <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{c.ceo||'-'} 대표 · {c.biz_type||'-'} · {fp ? `${fp.name} FP` : 'FP 없음'}</div>
                          </div>
                          <Badge bg={c.tm_status === '완료' ? C.greenLt : c.tm_status === '진행중' ? C.accentLt : C.bgAlt} color={c.tm_status === '완료' ? C.green : c.tm_status === '진행중' ? C.accent : C.muted}>{c.tm_status || '대기'}</Badge>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* 확인 필요 — 어기는 조건은 없는데 업체 칸이 비어 판정하지 못한 업체.
                    엄격 모드라 발송 불가 — 체크박스를 없애고 안내 문구만 보여준다(감사 B-6). */}
                {nearCompanies.length > 0 && (
                  <div style={{ marginTop:16, paddingTop:14, borderTop:`1px dashed ${C.border}` }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                      <span style={{ fontSize:12, fontWeight:700, color:'#9A5C05' }}>확인 필요 ({nearCompanies.length})</span>
                      <span style={{ fontSize:11, color:C.muted }}>업체 정보가 비어 판정하지 못했습니다 — 발송 불가</span>
                    </div>
                    {matchRes && Object.keys(matchRes.blockers).length > 0 && (
                      <div style={{ fontSize:11, color:C.muted, marginBottom:8 }}>
                        {Object.entries(matchRes.blockers).sort((a,b) => b[1]-a[1]).slice(0,3)
                          .map(([k,v]) => `${k} ${v}건`).join(' · ')} 만 채우면 대상이 됩니다
                      </div>
                    )}
                    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                      {nearCompanies.map(c => {
                        const fp = getFp(c);
                        return (
                          <div key={c.id}
                            style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 14px', borderRadius:R.md,
                              border:`1.5px solid ${C.border}`, background: C.bgAlt }}>
                            <span style={{ fontSize:14, flexShrink:0 }}>🔒</span>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontSize:13, fontWeight:600, color:C.sub }}>{c.name}</div>
                              <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{c.ceo||'-'} 대표 · {fp ? `${fp.name} FP` : 'FP 없음'}</div>
                            </div>
                            <Badge bg="#FBF3DD" color="#7A5A00">{(c.missing||[]).join('·') || '확인 필요'}</Badge>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              </div>
            </div>
          );
        })()}
      </div>

      {/* ═══ 등록/수정 모달 ═══ */}
      {showModal && (
        <div style={{ position:'fixed', inset:0, zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16, boxSizing:'border-box' }}>
          <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.4)' }} onClick={() => { setShowModal(false); setEditProg(null); }} />
          <div style={{ position:'relative', width:'100%', maxWidth:560, borderRadius:16, background:C.white, maxHeight:'92vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 8px 32px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'14px 20px', background:'linear-gradient(135deg, #111827, #1B3A5C)', borderRadius:'12px 12px 0 0', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span style={{ fontSize:15, fontWeight:700, color:'#fff' }}>🏛️ {editProg ? '사업 수정' : '사업 등록'}</span>
              <button onClick={() => { setShowModal(false); setEditProg(null); }} style={{ width:28, height:28, borderRadius:6, border:'1px solid rgba(255,255,255,0.3)', background:'rgba(255,255,255,0.15)', cursor:'pointer', color:'#fff', fontSize:13, display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
            </div>
            <div style={{ flex:1, overflowY:'auto', padding:'16px 20px' }}>
              <GovProgramForm form={form} setForm={setForm} programId={editProg} />
            </div>
            <div style={{ padding:'12px 20px', borderTop:`1px solid ${C.border}`, display:'flex', gap:8 }}>
              <Btn variant="secondary" onClick={() => { setShowModal(false); setEditProg(null); }} style={{ flex:1 }}>취소</Btn>
              <Btn onClick={handleSave} disabled={!canSaveGovForm(form)} style={{ flex:2 }}>{editProg ? '💾 저장' : '✅ 등록'}</Btn>
            </div>
          </div>
        </div>
      )}

      {/* ═══ 중복 경고 확인 모달(감사 A-9) — 저장은 이미 됐고, 같은 이름+기관 활성 공고가 있음을 알린다 ═══ */}
      {dupWarning && (
        <div style={{ position:'fixed', inset:0, zIndex:1200, display:'flex', alignItems:'center', justifyContent:'center', padding:16, boxSizing:'border-box' }}>
          <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.4)' }} onClick={() => setDupWarning(null)} />
          <div style={{ position:'relative', width:'100%', maxWidth:380, borderRadius:14, background:C.white, padding:'22px 22px 16px', boxShadow:'0 8px 32px rgba(0,0,0,0.2)', textAlign:'center' }}>
            <div style={{ fontSize:28, marginBottom:8 }}>⚠️</div>
            <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:6 }}>중복 등록일 수 있습니다</div>
            <div style={{ fontSize:12.5, color:C.sub, lineHeight:1.6, marginBottom:16 }}>
              「{dupWarning.name}」{dupWarning.agency ? ` (${dupWarning.agency})` : ''} 와 같은 이름·기관의 활성 공고가 이미 있습니다. 저장은 완료됐으니, 목록에서 중복 여부를 확인해 주세요.
            </div>
            <button onClick={() => setDupWarning(null)} style={{ width:'100%', padding:'10px 0', borderRadius:R.md, border:'none', background:C.accent, color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>확인</button>
          </div>
        </div>
      )}

      {/* SMS 미리보기 · 발송결과 모달 */}
      {showSmsPreview && selProg && (() => {
        const isFP = smsTarget === 'fp';
        const totalCount = sendItems.length;
        const smsCount = sendItems.filter(i => smsMsgType(i.message) === 'SMS').length;
        const lmsCount = totalCount - smsCount;
        const failedCount = sendPhase === 'done' ? sendItems.filter(i => sendResults[i.key] && !sendResults[i.key].ok).length : 0;
        return (
          <div style={{ position:'fixed', inset:0, zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16, boxSizing:'border-box' }}>
            <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.4)' }} onClick={closeSmsPreview} />
            <div style={{ position:'relative', width:'100%', maxWidth:560, maxHeight:'92vh', borderRadius:16, background:C.white, display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 8px 32px rgba(0,0,0,0.15)' }}>
              <div style={{ padding:'14px 20px', background: isFP ? 'linear-gradient(135deg, #111827, #1B3A5C)' : 'linear-gradient(135deg, #059669, #047857)', borderRadius:'12px 12px 0 0', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <span style={{ fontSize:15, fontWeight:700, color:'#fff' }}>{isFP ? '👥 FP에게 발송' : '🏢 회사에 발송'} ({totalCount}건)</span>
                <button onClick={closeSmsPreview} style={{ width:28, height:28, borderRadius:6, border:'1px solid rgba(255,255,255,0.3)', background:'rgba(255,255,255,0.15)', cursor:'pointer', color:'#fff', fontSize:13, display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
              </div>
              {totalCount > 0 && (
                <div style={{ padding:'8px 20px', background:C.bgAlt, borderBottom:`1px solid ${C.border}`, fontSize:11, color:C.sub, display:'flex', gap:10 }}>
                  <span>SMS {smsCount}건</span><span>·</span><span>LMS {lmsCount}건</span>
                  {sendPhase === 'done' && <><span>·</span><span style={{ color: failedCount>0 ? C.red : C.green, fontWeight:600 }}>{failedCount>0 ? `실패 ${failedCount}건` : '전체 성공'}</span></>}
                </div>
              )}
              <div style={{ flex:1, overflowY:'auto', padding:'16px 20px' }}>
                {totalCount === 0 && <div style={{ padding:24, textAlign:'center', color:C.muted, fontSize:T.sm }}>{isFP ? '발송 가능한 FP가 없습니다 (연락처 없음)' : '발송 가능한 업체가 없습니다 (대표 연락처 없음)'}</div>}
                {sendItems.map(item => {
                  const result = sendResults[item.key];
                  const mtype = smsMsgType(item.message);
                  return (
                    <div key={item.key} style={{ marginBottom:12, borderRadius:R.md, border:`1px solid ${result && !result.ok ? C.red : C.border}`, overflow:'hidden' }}>
                      <div style={{ padding:'8px 14px', background:C.bgAlt, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:4 }}>
                        <span style={{ fontSize:12, fontWeight:700 }}>📱 {item.label}</span>
                        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                          <Badge bg={mtype==='LMS' ? '#FFF3E0' : C.accentLt} color={mtype==='LMS' ? '#B45309' : C.accent} style={{ fontSize:10 }}>{mtype} · {smsByteLen(item.message)}B</Badge>
                          {result && (result.ok ? <Badge bg={C.greenLt} color={C.green} style={{ fontSize:10 }}>✓ 성공</Badge> : <Badge bg={C.redLt} color={C.red} style={{ fontSize:10 }}>✗ 실패</Badge>)}
                        </div>
                      </div>
                      <div style={{ padding:'10px 14px', fontSize:12, lineHeight:1.7, whiteSpace:'pre-wrap', color:C.sub }}>{item.message}</div>
                      {result && !result.ok && result.error && (
                        <div style={{ padding:'6px 14px 10px', fontSize:11, color:C.red, borderTop:`1px dashed ${C.border}` }}>실패 사유: {result.error}</div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{ padding:'12px 20px', borderTop:`1px solid ${C.border}`, display:'flex', gap:8 }}>
                {sendPhase === 'preview' ? (<>
                  <button onClick={closeSmsPreview} style={{ flex:1, padding:'10px 0', borderRadius:R.md, border:`1px solid ${C.border}`, background:C.white, color:C.sub, fontSize:12, fontWeight:600, cursor:'pointer' }}>취소</button>
                  <button onClick={handleSmsSend} disabled={smsSending || totalCount === 0} style={{ flex:2, padding:'10px 0', borderRadius:R.md, border:'none', background: smsSending ? C.muted : isFP ? C.accent : '#059669', color:'#fff', fontSize:13, fontWeight:700, cursor: smsSending ? 'default' : 'pointer' }}>
                    {smsSending ? '발송 중...' : `📨 ${totalCount}건 발송`}
                  </button>
                </>) : (<>
                  <button onClick={closeSmsPreview} style={{ flex:1, padding:'10px 0', borderRadius:R.md, border:`1px solid ${C.border}`, background:C.white, color:C.sub, fontSize:12, fontWeight:600, cursor:'pointer' }}>닫기</button>
                  {failedCount > 0 && (
                    <button onClick={handleResendFailed} disabled={smsSending} style={{ flex:2, padding:'10px 0', borderRadius:R.md, border:'none', background: smsSending ? C.muted : C.red, color:'#fff', fontSize:13, fontWeight:700, cursor: smsSending ? 'default' : 'pointer' }}>
                      {smsSending ? '재발송 중...' : `↻ 실패분만 재발송 (${failedCount}건)`}
                    </button>
                  )}
                </>)}
              </div>
            </div>
          </div>
        );
      })()}

      {/* 공고문 보기 창 — 오른쪽 서랍(PC) · 전체 화면(모바일) · 바깥 누르거나 Esc 로 닫기 */}
      {docOpen && docGroupId && selProg && (
        <div role="dialog" aria-modal="true" aria-label="공고문 보기" onClick={() => setDocOpen(false)}
          style={{ position:'fixed', inset:0, zIndex:9000, background:'rgba(15,23,42,0.32)', display:'flex', justifyContent:'flex-end' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ width:'min(980px, 100vw)', height:'100%', background:C.white, boxShadow:'-12px 0 32px rgba(15,23,42,0.18)', display:'flex', flexDirection:'column', minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px 10px 18px', borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:T.xs, color:C.muted, fontWeight:600 }}>공고문</div>
                <div style={{ fontSize:T.md, fontWeight:700, color:C.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={selProg.name}>{selProg.name}</div>
              </div>
              <button type="button" onClick={() => setDocOpen(false)} aria-label="닫기" title="닫기 (Esc)"
                style={{ width:34, height:34, borderRadius:R.sm, border:`1px solid ${C.border}`, background:C.white, cursor:'pointer', color:C.sub, fontSize:16, lineHeight:1, flexShrink:0 }}>✕</button>
            </div>
            <div style={{ flex:1, minHeight:0, display:'flex', minWidth:0 }}>
              <GovFeedDocViewer groupId={docGroupId} focus={null} onPick={() => {}} />
            </div>
          </div>
        </div>
      )}

      {toast && <div style={{ position:'fixed', bottom:24, left:'50%', transform:'translateX(-50%)', padding:'10px 20px', borderRadius:R.md, background:C.text, color:C.white, fontSize:T.sm, fontWeight:600, zIndex:9999 }}>{toast}</div>}
    </div>
    </div>
  );
}
